<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCross from '@/components/icon/IconCross.vue'

const props = defineProps({
	modelValue: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: 'Search...',
	},
	enabled: {
		type: Boolean,
		default: true,
	},
})

const emit = defineEmits({
	'update:modelValue': () => true,
})

const inputRef = ref(null)
const formRef = ref(null)
const focused = ref(false)

const query = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
})

const showSlashHint = computed(() => !query.value && !focused.value)

const onGlobalKey = (e) => {
	if (!props.enabled) return
	if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
	const el = document.activeElement
	const tag = (el?.tagName || '').toLowerCase()
	if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return
	e.preventDefault()
	inputRef.value?.focus?.()
}

const onReset = (e) => {
	e.preventDefault()
	emit('update:modelValue', '')
	nextTick(() => inputRef.value?.focus?.())
}

onMounted(async () => {
	window.addEventListener('keydown', onGlobalKey)
	await nextTick()
	formRef.value?.addEventListener('reset', onReset)
})
onBeforeUnmount(() => {
	window.removeEventListener('keydown', onGlobalKey)
	formRef.value?.removeEventListener('reset', onReset)
})

defineExpose({
	focus: () => inputRef.value?.focus?.(),
	blur: () => inputRef.value?.blur?.(),
})
</script>

<template>
	<form ref="formRef" class="relative flex items-center w-full sm:w-64" @submit.prevent>
		<icon-base class="text-surface-8 absolute left-3 shrink-0 z-10" fill="none">
			<icon-search />
		</icon-base>
		<FormInput
			ref="inputRef"
			v-model="query"
			type="search"
			enterkeyhint="search"
			:placeholder="placeholder"
			customClasses="!px-8"
			@focus="focused = true"
			@blur="focused = false"
		/>
		<button
			v-if="query"
			type="reset"
			aria-label="Clear search"
			class="absolute right-2 z-10 inline-flex items-center justify-center size-7 text-surface-text-1 hover:text-white"
		>
			<icon-base class="shrink-0" fill="none">
				<icon-cross />
			</icon-base>
		</button>
		<kbd
			v-else-if="showSlashHint"
			class="hidden sm:flex absolute right-2 z-10 items-center h-5 px-1.5 rounded border border-surface-4 text-[10px] text-surface-7 pointer-events-none select-none"
		>/</kbd>
	</form>
</template>
